import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MenuModule } from '@openng/optimus-ui/menu';
import { MessageModule } from '@openng/optimus-ui/message';
import { SelectModule } from '@openng/optimus-ui/select';
import { TagModule } from '@openng/optimus-ui/tag';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import {
  AbilityService,
  CurrencyFormatPipe,
  DropdownData,
  GenericDropdownComponent,
  ImagePreview,
  ImagePreviewItem,
  LocalStorageManagerService,
  rxResourceFixed,
  SpinnerService
} from '@zayan-erp/shared';

import {
  DsmProductOption,
  DsmSelectedProductEntry,
  OptionFilters,
  OptionFilterValue
} from '../../../models/dsm-deal-options.models';
import { DsmDealOptionProduct } from '../../../models/dsm-deal-options-view.models';
import { DsmDealOptionFiltersService } from '../../../services/dsm-deal-option-filters.service';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';

const DESIGN_CENTRE_CHANNEL = 1;

/** A subcategory dropdown item that remembers its parent, so the list can narrow to the picked
 *  category without another read. */
export interface DsmScopedSubcategoryOption extends DropdownData {
  categoryId: string | null;
}

/**
 * The option browsing surface: the filter row, the sort/search row and the product grid.
 *
 * Shared by the Options tab (unrestricted) and the Scenarios tab (restricted to the picked
 * step's categories and subcategories). It only browses — picking a product is handed straight
 * back to the host through `productPicked`, so the add-to-addendum flow stays in one place.
 */
@Component({
  selector: 'dsm-add-option-options-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    FloatLabelModule,
    InputTextModule,
    MenuModule,
    MessageModule,
    SelectModule,
    TagModule,
    TooltipModule,
    GenericDropdownComponent,
    ImagePreview,
    CurrencyFormatPipe
  ],
  templateUrl: './dsm-add-option-options-panel.component.html'
})
export class DsmAddOptionOptionsPanelComponent {
  private readonly modelElevationProductsService = inject(DsmModelElevationProductsService);
  private readonly dealOptionFiltersService = inject(DsmDealOptionFiltersService);
  private readonly abilityService = inject(AbilityService);
  private readonly localStorageService = inject(LocalStorageManagerService);
  readonly #spinner = inject(SpinnerService);

  readonly modelId = input.required<string>();
  readonly elevationId = input.required<string>();
  readonly lotId = input.required<string | null>();
  readonly addedProductIds = input.required<string[]>();
  readonly addedProducts = input<DsmDealOptionProduct[]>([]);
  /** The host's working set, so cards can show their selected badge and count. */
  readonly selectedEntries = input<DsmSelectedProductEntry[]>([]);
  readonly isPackageSource = input(false);
  /** Drives the grid column count — the host's Selected Products panel steals width when pinned. */
  readonly sidebarPinned = input(false);

  /**
   * When set, the dropdowns offer exactly these items instead of the ones derived from the deal's
   * option filters. The Scenarios tab supplies them so a customer sees the step's DSM labels
   * rather than the product library's own technical category names. Ids are the same either way,
   * so loading products off a selection is unaffected. `null` leaves the filters as they are.
   */
  readonly categoryOptionsOverride = input<DropdownData[] | null>(null);
  readonly subcategoryOptionsOverride = input<DsmScopedSubcategoryOption[] | null>(null);

  readonly filtersVisible = model<boolean>(true);

  readonly productPicked = output<DsmProductOption>();

  readonly canApplyCutoff = computed(() => this.abilityService.ability.can('apply-cutoff', 'aps-addendum'));
  readonly cutoffOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  readonly sortByOptions = [
    { id: 'code', firstLabel: 'Option Code' },
    { id: 'unitPrice', firstLabel: 'Unit Price' },
    { id: 'description', firstLabel: 'Description' },
    { id: 'category', firstLabel: 'Category' },
    { id: 'subcategory', firstLabel: 'Subcategory' },
    { id: 'type', firstLabel: 'Type' },
    { id: 'uom', firstLabel: 'UoM' },
    { id: 'selectionRequired', firstLabel: 'Attribute Required' }
  ];

  readonly isLoadingOptions = computed(() => this.#spinner.isButtonLoading('dsm-load-options'));

  readonly viewMode = signal<'grid' | 'list'>(this.localStorageService.getAddOptionViewModeFromStorage());

  readonly searchTerm = signal<string>('');
  readonly selectedRoomId = signal<string | null>(null);
  readonly selectedCategoryId = signal<string | null>(null);
  readonly selectedSubcategoryId = signal<string | null>(null);
  readonly selectedApplyCutoff = signal(false);
  /** Category is required, so its presence alone is what gates the first load and every refetch. */
  readonly hasCategorySelected = computed(() => !!this.selectedCategoryId());
  readonly sortBy = signal<string>('code');
  readonly searchExpanded = signal(false);
  readonly sortMenuOpen = signal(false);
  readonly sortApplied = signal(false);

  readonly sortLabel = computed(() => this.sortByOptions.find(o => o.id === this.sortBy())?.firstLabel ?? '');

  readonly sortMenuItems = computed(() =>
    this.sortByOptions.map(o => ({
      label: o.firstLabel,
      styleClass: this.sortBy() === o.id ? 'dsm-sort-selected' : undefined,
      command: () => {
        this.sortBy.set(o.id);
        this.sortApplied.set(true);
      }
    }))
  );

  readonly activeFilterCount = computed(() => {
    let count = 0;
    if (this.selectedCategoryId()) count++;
    if (this.selectedSubcategoryId()) count++;
    if (this.selectedRoomId()) count++;
    if (this.canApplyCutoff() && this.selectedApplyCutoff()) count++;
    return count;
  });

  readonly filtersResource = rxResourceFixed({
    params: () => ({
      modelId: this.modelId(),
      elevationId: this.elevationId(),
      roomId: this.selectedRoomId(),
      categoryId: this.selectedCategoryId(),
      subcategoryId: this.selectedSubcategoryId(),
      lotId: this.lotId()
    }),
    defaultValue: { categories: [], subcategories: [], rooms: [], hasAnyOptions: false } as OptionFilters,
    stream: ({ params }) => {
      if (!params.modelId || !params.elevationId) {
        return of<OptionFilters>({ categories: [], subcategories: [], rooms: [], hasAnyOptions: false });
      }
      return this.dealOptionFiltersService.getOptionFilters(params.modelId, params.elevationId, {
        roomId: params.roomId,
        categoryId: params.categoryId,
        subcategoryId: params.subcategoryId,
        catalogChannel: DESIGN_CENTRE_CHANNEL,
        lotId: params.lotId
      });
    }
  });

  readonly #initialCategories = signal<DropdownData[] | null>(null);

  readonly categories = computed<DropdownData[]>(() => {
    const override = this.categoryOptionsOverride();
    if (override) return override;
    return this.#initialCategories() ?? this.#toCategoryOptions(this.filtersResource.value()?.categories ?? []);
  });

  readonly subcategories = computed<DropdownData[]>(() => {
    const override = this.subcategoryOptionsOverride();
    if (override) {
      // The unrestricted list narrows server-side as the category changes; the override list
      // already holds every subcategory the step covers, so narrow it here instead.
      const categoryId = this.selectedCategoryId();
      return categoryId ? override.filter(option => option.categoryId === categoryId) : override;
    }
    return (this.filtersResource.value()?.subcategories ?? [])
      .filter(sc => !sc.isHidden)
      .map(sc => ({
        id: sc.id,
        firstLabel: sc.effectiveLabel
      }));
  });

  readonly rooms = computed<DropdownData[]>(() =>
    (this.filtersResource.value()?.rooms ?? []).map(r => ({
      id: r.id,
      firstLabel: r.name,
      secondLabel: r.code
    }))
  );

  readonly hasAnyOptions = computed(() => this.filtersResource.value()?.hasAnyOptions ?? false);
  readonly filtersLoading = computed(() => this.filtersResource.isLoading());
  readonly filtersHasError = computed(() => !!this.filtersResource.error());

  /** Category is required and is part of `params`, so nothing is fetched until it's picked; every
   *  filter change afterwards (category included) is itself a params change, so this refetches
   *  automatically with no explicit trigger. */
  readonly productsResource = rxResourceFixed({
    params: () => ({
      modelId: this.modelId(),
      elevationId: this.elevationId(),
      lotId: this.lotId(),
      roomId: this.selectedRoomId(),
      categoryId: this.selectedCategoryId(),
      subcategoryId: this.selectedSubcategoryId(),
      applyCutoff: this.selectedApplyCutoff()
    }),
    defaultValue: [] as DsmProductOption[],
    stream: ({ params }) => {
      if (!params.categoryId) {
        return of<DsmProductOption[]>([]);
      }
      return this.modelElevationProductsService.getModelElevationProducts(
        params.modelId,
        params.elevationId,
        {
          lotId: params.lotId ?? undefined,
          roomId: params.roomId ?? undefined,
          categoryId: params.categoryId ?? undefined,
          subcategoryId: params.subcategoryId ?? undefined,
          applyCutoff: params.applyCutoff || undefined
        },
        true,
        'dsm-load-options'
      );
    }
  });

  readonly optionsLoadError = computed(() => !!this.productsResource.error());

  readonly products = computed(() => {
    if (!this.hasCategorySelected()) {
      return [];
    }
    const term = this.searchTerm().toLowerCase();
    const filtered = this.productsResource
      .value()
      .filter(
        p =>
          p.code.toLowerCase().includes(term) ||
          p.description.toLowerCase().includes(term) ||
          p.type?.toLowerCase()?.includes(term) ||
          p.uom.toLowerCase().includes(term) ||
          p.category?.toLowerCase().includes(term) ||
          p.subcategory?.toLowerCase().includes(term) ||
          p.unitPrice.toString().includes(term)
      );
    const key = this.sortBy();
    return [...filtered].sort((a, b) => {
      if (key === 'unitPrice') return (a.unitPrice ?? 0) - (b.unitPrice ?? 0);
      if (key === 'description') return (a.description ?? '').localeCompare(b.description ?? '');
      if (key === 'category') return (a.category ?? '').localeCompare(b.category ?? '');
      if (key === 'subcategory') return (a.subcategory ?? '').localeCompare(b.subcategory ?? '');
      if (key === 'type') return (a.type ?? '').localeCompare(b.type ?? '');
      if (key === 'uom') return (a.uom ?? '').localeCompare(b.uom ?? '');
      if (key === 'selectionRequired') {
        const first = a.selectionRequired ? 1 : 0;
        const second = b.selectionRequired ? 1 : 0;
        return first - second;
      }
      return (a.code ?? '').localeCompare(b.code ?? '');
    });
  });

  readonly defaultProductImageUrl = 'assets/images/img-placeholder.png';

  constructor() {
    effect(() => {
      this.localStorageService.setAddOptionViewModeToStorage(this.viewMode());
    });

    effect(() => {
      const categories = this.filtersResource.value()?.categories ?? [];
      if (!this.#initialCategories() && categories.length > 0) {
        this.#initialCategories.set(this.#toCategoryOptions(categories));
      }
    });

    effect(() => {
      if (this.selectedCategoryId()) {
        this.filtersVisible.set(false);
      }
    });

    // Re-open the filters panel (collapsed on mobile once a category is picked) so the user can
    // adjust filters and retry when a fetch fails.
    effect(() => {
      if (this.productsResource.error()) {
        this.filtersVisible.set(true);
      }
    });
  }

  /** Buyers see the DSM label, never the technical library name, and hidden categories not at all. */
  #toCategoryOptions(categories: OptionFilterValue[]): DropdownData[] {
    return categories.filter(c => !c.isHidden).map(c => ({ id: c.id, firstLabel: c.effectiveLabel }));
  }

  toggleSearch(input: HTMLInputElement): void {
    if (this.searchExpanded() && !this.searchTerm()) {
      this.searchExpanded.set(false);
      return;
    }
    this.searchExpanded.set(true);
    setTimeout(() => input.focus());
  }

  onSearchBlur(): void {
    if (!this.searchTerm()) this.searchExpanded.set(false);
  }

  onSearchKeyup(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  refreshOptions(): void {
    this.productsResource.reload();
  }

  retryFilters(): void {
    this.filtersResource.reload();
  }

  pickProduct(product: DsmProductOption): void {
    if (this.isPackageSource() && this.isProductSelected(product)) return;
    this.productPicked.emit(product);
  }

  getProductSelectedCount(product: DsmProductOption): number {
    return this.selectedEntries().filter(e => e.product.productId === product.productId).length;
  }

  isProductNewlySelected(product: DsmProductOption): boolean {
    return this.selectedEntries().some(entry => entry.product.productId === product.productId);
  }

  isProductSelected(product: DsmProductOption): boolean {
    const newlySelected = this.selectedEntries().some(
      entry =>
        entry.product.productId === product.productId ||
        entry.product.modelElevationProductsId === product.modelElevationProductsId
    );
    if (newlySelected) return true;
    // Package source: allow re-picking products already in the package (raises count).
    // Still block the same product twice in one session via newlySelected above.
    if (this.isPackageSource()) return false;
    if (this.addedProductIds().includes(product.productId ?? '')) return true;
    return false;
  }

  getProductImageUrl(product: DsmProductOption): string {
    const first = product.images && product.images.length > 0 ? product.images[0] : null;
    return first?.document?.directUrl ?? this.defaultProductImageUrl;
  }

  getProductPreviewImages(product: DsmProductOption): ImagePreviewItem[] {
    return (product.images ?? []).map(image => ({
      itemImageSrc: image.document.directUrl,
      thumbnailImageSrc: image.document.directUrl,
      alt: product.code
    }));
  }

  hasProductImages(product: DsmProductOption): boolean {
    return !!product.images?.length;
  }
}
